import { useContext, useCallback } from 'react'

import { AlertDialog, ConfirmDialog } from '@components/designSystem/Dialog/DialogVariants'

import { DialogContext, type BaseDialogProps, type DialogComponent } from './DialogContext'

interface DialogHandler<T extends BaseDialogProps> {
  showDialog: (props?: Omit<T, 'onClose'>) => void
  hideDialog: () => void
  isOpen: boolean
  setPreventBackdropClose: (prevent: boolean) => void
}

/**
 * Hook for managing dialog state and interactions.
 * @param Component - The dialog component to render
 * @param defaultProps - Default props to pass to the dialog component
 * @returns Dialog handler with show/hide methods and open state
 */
export function useDialog<T extends BaseDialogProps>(
  Component: DialogComponent<T>,
  defaultProps?: Omit<T, 'onClose'>
): DialogHandler<T> {
  const context = useContext(DialogContext)

  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider')
  }

  const { showDialog: contextShowDialog, hideDialog, isOpen, setPreventBackdropClose } = context

  const showDialog = useCallback((props?: Omit<T, 'onClose'>) => {
    const mergedProps = defaultProps ? { ...defaultProps, ...props } : props
    contextShowDialog(Component, mergedProps)
  }, [Component, contextShowDialog, defaultProps])

  return {
    showDialog,
    hideDialog,
    isOpen,
    setPreventBackdropClose,
  }
}

export function useAlertDialog() {
  return useDialog(AlertDialog)
}

export function useConfirmDialog() {
  return useDialog(ConfirmDialog)
}